import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";

function safeJsonParse(str) {
    try {
        return JSON.parse(str);
    } catch {
        return null;
    }
}

// WS dinámico: usa la variable de entorno REACT_APP_WS_URL si existe, 
// de lo contrario cae a ws://localhost:8000 o wss:// según el protocolo actual.
const WS_BASE = process.env.REACT_APP_WS_URL || 
    `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.hostname}:8000`;

export default function useChat(salaId) {
    const token = (localStorage.getItem("token") || "").replace(/^Bearer\s+/i, "");
    const [mensajes, setMensajes] = useState([]);

    const wsRef = useRef(null);
    const currentSalaRef = useRef(null);

    const fetchMensajes = useCallback(async () => {
        if (!salaId) {
            setMensajes([]);
            return;
        }
        try {
            const { data } = await api.get(`/chat/mensajes/${salaId}`);
            if (Array.isArray(data)) {
                // Si la sala ha cambiado mientras cargaba, ignora el resultado
                if (currentSalaRef.current !== salaId) return;

                setMensajes((prev) => {
                    // Combinar historial con mensajes que hayan llegado por WS mientras cargaba
                    const wsMessages = prev.filter(m =>
                        Number(m.idsala) === Number(salaId) &&
                        !data.some(dm => dm.idmensaje === m.idmensaje)
                    );
                    return [...data, ...wsMessages];
                });
            }
        } catch {
            // No resetea a [] si ya hay mensajes (pueden ser de WS)
        }
    }, [salaId]);

    const wsSend = useCallback(
        (payload) => {
            const ws = wsRef.current;
            if (!ws) return false;
            if (ws.readyState !== WebSocket.OPEN) return false;
            if (currentSalaRef.current !== salaId) return false;

            try {
                ws.send(JSON.stringify(payload));
                return true;
            } catch {
                return false;
            }
        },
        [salaId]
    );

    useEffect(() => {
        const setLocal = (key, val) => {
            const current = localStorage.getItem(key);
            if (current !== String(val)) {
                localStorage.setItem(key, String(val));
            }
        };

        setLocal("chat_on_page", "1");
        setLocal("chat_current_sala", String(salaId || 0));

        if (!salaId || !token) {
            setMensajes([]);
            return () => {
                setLocal("chat_on_page", "0");
                setLocal("chat_current_sala", "0");
            };
        }

        currentSalaRef.current = salaId;
        setMensajes([]);
        fetchMensajes();

        const wsUrl = `${WS_BASE}/chat/ws/${salaId}?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (evt) => {
            const data = safeJsonParse(evt.data);
            if (!data?.tipo) return;

            if (data.idsala && Number(data.idsala) !== Number(salaId)) return;

            if (data.tipo === "message" || data.tipo === "adjunto") {
                setMensajes((prev) => {
                    if (prev.some((m) => m.idmensaje === data.idmensaje)) return prev;
                    return [...prev, data];
                });
                return;
            }

            if (data.tipo === "editar") {
                setMensajes((prev) =>
                    prev.map((m) =>
                        m.idmensaje === data.idmensaje
                            ? { ...m, contenido: data.contenido }
                            : m
                    )
                );
                return;
            }

            if (data.tipo === "borrar") {
                setMensajes((prev) => prev.filter((m) => m.idmensaje !== data.idmensaje));
            }
        };

        return () => {
            const setOff = (key) => {
                if (localStorage.getItem(key) !== "0") {
                    localStorage.setItem(key, "0");
                }
            };
            setOff("chat_on_page");
            setOff("chat_current_sala");

            if (wsRef.current) {
                try {
                    wsRef.current.onopen = null;
                    wsRef.current.onmessage = null;
                    wsRef.current.onclose = null;
                    wsRef.current.onerror = null;
                    wsRef.current.close(1000, "unmount");
                } catch {}
                wsRef.current = null;
            }
        };
    }, [salaId, fetchMensajes, token]);

    const enviarMensaje = useCallback(
        (texto) => {
            const t = (texto || "").trim();
            if (!t || !salaId) return;

            const sent = wsSend({ tipo: "nuevo", contenido: t });
            if (!sent) {
                api.post(`/chat/mensajes/${salaId}`, { contenido: t }).catch(() => {});
            }
        },
        [wsSend, salaId]
    );

    const editMensaje = useCallback(
        async (idmensaje, contenido) => {
            const t = (contenido || "").trim();
            if (!idmensaje || !t) return;

            setMensajes((prev) =>
                prev.map((m) => (m.idmensaje === idmensaje ? { ...m, contenido: t } : m))
            );

            const sent = wsSend({ tipo: "editar", idmensaje, contenido: t });
            if (!sent) {
                try {
                    await api.put(`/chat/mensajes/${idmensaje}`, { contenido: t });
                } catch {
                    fetchMensajes();
                }
            }
        },
        [wsSend, fetchMensajes]
    );

    const deleteMensaje = useCallback(
        async (idmensaje) => {
            if (!idmensaje) return;

            setMensajes((prev) => prev.filter((m) => m.idmensaje !== idmensaje));

            const sent = wsSend({ tipo: "borrar", idmensaje });
            if (!sent) {
                try {
                    await api.delete(`/chat/mensajes/${idmensaje}`);
                } catch {
                    fetchMensajes();
                }
            }
        },
        [wsSend, fetchMensajes]
    );

    return { mensajes, fetchMensajes, enviarMensaje, editMensaje, deleteMensaje };
}